import React, { useState, useRef, useEffect, useCallback } from "react";
import { List, ListItem, ListItemText, ListItemIcon, makeStyles, Divider } from "@material-ui/core";
import { Link, useLocation } from "react-router-dom";
import clsx from "clsx";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  listItem: {
    paddingLeft: theme.spacing(3),
    flexShrink: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    borderLeft: "3px solid transparent",
    transition: "background-color 0.15s ease, border-left 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemActive: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  listItemIcon: {
    minWidth: 32,
  },
  listItemText: {
    overflow: "hidden",
    minWidth: 0,
  },
  marqueeWrap: {
    overflow: "hidden",
    display: "block",
    width: "100%",
  },
  marqueeText: {
    display: "inline-block",
    whiteSpace: "nowrap",
  },
}));

function MarqueeText({ text }) {
    const classes = useStyles();
    const textRef = useRef(null);
    const wrapRef = useRef(null);
    const [overflows, setOverflows] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [animating, setAnimating] = useState(false);
    const [scrollDuration, setScrollDuration] = useState(3);
    const delayTimer = useRef(null);

    const checkOverflow = useCallback(() => {
        if (textRef.current && wrapRef.current && text.length >= 20) {
            const isOverflow = textRef.current.scrollWidth > wrapRef.current.clientWidth;
            setOverflows(isOverflow);
            if (isOverflow) {
                const overflow = textRef.current.scrollWidth - wrapRef.current.clientWidth;
                setScrollDuration(Math.max(4, overflow / 15));
            }
        } else {
            setOverflows(false);
        }
    }, [text]);

    useEffect(() => {
        // Check after subdrawer animation settles
        const timer = setTimeout(checkOverflow, 350);
        const ro = wrapRef.current && window.ResizeObserver
            ? new ResizeObserver(checkOverflow) : null;
        if (ro && wrapRef.current) ro.observe(wrapRef.current);
        return () => { clearTimeout(timer); if (ro) ro.disconnect(); };
    }, [checkOverflow]);

    useEffect(() => {
        if (hovered && overflows) {
            delayTimer.current = setTimeout(() => setAnimating(true), 1000);
        } else {
            clearTimeout(delayTimer.current);
            setAnimating(false);
        }
        return () => clearTimeout(delayTimer.current);
    }, [hovered, overflows]);

    const animStyle = animating
        ? { animation: `subDrawerMarquee ${scrollDuration}s linear infinite` }
        : {};

    return (
        <span
            ref={wrapRef}
            className={classes.marqueeWrap}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <span ref={textRef} className={classes.marqueeText} style={animStyle}>
                {text}
            </span>
            {overflows && (
                <style>{`
                    @keyframes subDrawerMarquee {
                        0% { transform: translateX(0); opacity: 1; }
                        80% { transform: translateX(-100%); opacity: 1; }
                        85% { transform: translateX(-100%); opacity: 0; }
                        86% { transform: translateX(0); opacity: 0; }
                        90% { transform: translateX(0); opacity: 1; }
                        100% { transform: translateX(0); opacity: 1; }
                    }
                `}</style>
            )}
        </span>
    );
}

export const SubDrawer = (props) => {
  const classes = useStyles();
  const location = useLocation();

  const isActiveRoute = useCallback(
    (link) => {
      if (!link) return false;
      if (link === "/dashboard") return location.pathname === "/dashboard";
      return location.pathname.startsWith(link);
    },
    [location.pathname]
  );

  return (
    <List>
      {props.items.map((item, index) => {
        // Check permissions
        const hasPermission = props.roles.includes(item?.roles) || _.isUndefined(item.roles);
        if (!hasPermission) return null;

        // Render divider
        if (item.title === "DIVIDER") {
          return <Divider key={`divider-${index}`} />;
        }

        const isActive = isActiveRoute(item.link);
        const hasSubItems = item.items && item.items.length > 0;

        return (
          <ListItem
            key={item.title || index}
            className={clsx(classes.listItem, { [classes.listItemActive]: isActive })}
            onClick={() => {
              if (hasSubItems) {
                props.openSubSubDrawer(
                  <SubDrawer roles={props.roles} items={item.items} level={item.level} />
                );
              } else {
                props.onClick();
              }
            }}
            component={!hasSubItems ? Link : "div"}
            to={!hasSubItems ? item.link : undefined}
            button
          >
            {item.icon && (
              <ListItemIcon className={classes.listItemIcon}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText className={classes.listItemText} primary={<MarqueeText text={item.title} />} />
          </ListItem>
        );
      })}
    </List>
  );
};
